import Phaser from "phaser";
import { makeDawnSkyTexture, makeGlowTexture, makeLanternTexture, makeSkyTexture } from "../textures";
import type { Ambience } from "../audio";
import { LEVELS } from "../levels";
import { VIEW_HEIGHT, VIEW_WIDTH } from "./dimensions";

interface EndingInitData {
  ambience: Ambience;
  resets: number;
  /** Flawless levels (every mote found) completed this run. */
  flawless?: number;
  /** Lanterns planted in the hollow this run. */
  lanterns?: number;
}

/**
 * The payoff for finishing the last level: the thing the whole game has been
 * building - light growing until it fills the frame - happens one final time,
 * at full scale, uninterrupted. Wordless except for one short line, per
 * SPEC.md's "text is a fallback, not a feature."
 */
const BEST_RESETS_KEY = "start-of-glow-best-resets";

export class EndingScene extends Phaser.Scene {
  private ambience!: Ambience;
  private resets = 0;
  private flawless = 0;
  private lanterns = 0;
  private isNewBest = false;

  constructor() {
    super("ending");
  }

  init(data: EndingInitData): void {
    this.ambience = data.ambience;
    this.resets = data.resets ?? 0;
    this.flawless = data.flawless ?? 0;
    this.lanterns = data.lanterns ?? 0;
    this.isNewBest = this.recordBest(this.resets);
  }

  /**
   * localStorage only, no backend, no account - the whole game already has
   * neither. Only worth celebrating against a PRIOR run: a first-ever clear
   * quietly sets the baseline rather than announcing a "best" with nothing
   * to compare against. Wrapped defensively - private browsing or storage
   * being unavailable should never be able to break the ending.
   */
  private recordBest(resets: number): boolean {
    try {
      const raw = window.localStorage.getItem(BEST_RESETS_KEY);
      const prevBest = raw === null ? null : Number(raw);
      const hadPrior = prevBest !== null && Number.isFinite(prevBest);
      const isBest = !hadPrior || resets < (prevBest as number);
      if (isBest) window.localStorage.setItem(BEST_RESETS_KEY, String(resets));
      return hadPrior && isBest;
    } catch {
      return false;
    }
  }

  preload(): void {
    makeSkyTexture(this, "sky", VIEW_WIDTH, VIEW_HEIGHT, 11);
    makeGlowTexture(this, "wisp", 85, "rgba(255,255,255,1)", "rgba(150,214,255,0.55)");
    makeLanternTexture(this, "lantern");
    makeDawnSkyTexture(this, "dawn-sky", VIEW_WIDTH, VIEW_HEIGHT);
  }

  create(): void {
    const dawn = this.lanterns > 0;
    this.lights.enable().setAmbientColor(dawn ? 0x1a1210 : 0x0a0d18);
    this.cameras.main.setBackgroundColor(dawn ? 0x0c0808 : 0x05060c);

    this.add.image(VIEW_WIDTH / 2, VIEW_HEIGHT / 2, dawn ? "dawn-sky" : "sky").setDepth(-100);

    const sunY = dawn ? VIEW_HEIGHT * 0.62 : VIEW_HEIGHT / 2;
    const wisp = this.add
      .image(VIEW_WIDTH / 2, sunY, "wisp")
      .setBlendMode(Phaser.BlendModes.ADD)
      .setScale(0.5)
      .setDepth(10);
    const light = this.lights.addLight(wisp.x, wisp.y, 300, 0xffe6bf, 1.4);

    this.ambience.setStorm(false);
    this.ambience.ending();

    this.tweens.add({
      targets: wisp,
      scale: 3.2,
      duration: 4200,
      ease: "Sine.easeOut",
    });
    this.tweens.add({
      targets: light,
      intensity: 2.6,
      radius: 900,
      duration: 4200,
      ease: "Sine.easeOut",
    });

    // Warm parchment lettering, same family as the HUD and level card. The
    // first cut used dark browns (#2a2013 etc.) meant to read as silhouettes
    // against the wisp's bloom - ~1.5:1 contrast against the sky wherever
    // the bloom is dimmer than intended (software rasterizers provably, and
    // any display that tones the additive glow down), which made the run's
    // own closing stats the least readable text in the game (found at the
    // 08-24 judging-day playtest).
    if (this.lanterns > 0) {
      const count = Math.min(this.lanterns, 7);
      for (let i = 0; i < count; i += 1) {
        const t = count === 1 ? 0.5 : i / (count - 1);
        const x = 160 + t * (VIEW_WIDTH - 320);
        const y = VIEW_HEIGHT * 0.38 + Math.sin(t * Math.PI) * -28;
        this.add.image(x, y, "lantern").setScale(1.15).setDepth(8);
      }
    }

    this.add
      .text(
        VIEW_WIDTH / 2,
        VIEW_HEIGHT * 0.72,
        this.lanterns > 0 ? "the hollow kept every light you left" : "the forest remembers the light",
        {
        fontFamily: "Georgia, 'Times New Roman', serif",
        fontSize: "26px",
        color: "#fff6e0",
      })
      .setOrigin(0.5)
      .setAlpha(0.92)
      .setDepth(20);

    // Only worth a line when it happened - a run that skipped motes gets no
    // scolding, just the resets line it would have gotten anyway.
    if (this.flawless > 0) {
      const flawlessText =
        this.flawless >= LEVELS.length
          ? "you found every mote there was"
          : `${this.flawless} of ${LEVELS.length} clearings gave up every mote`;
      const flawlessLine = this.add
        .text(VIEW_WIDTH / 2, VIEW_HEIGHT * 0.845, flawlessText, {
          fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
          fontSize: "14px",
          color: "#d9c9a3",
        })
        .setOrigin(0.5)
        .setAlpha(0)
        .setDepth(20);
      this.tweens.add({ targets: flawlessLine, alpha: 0.65, duration: 1400, delay: 2600, ease: "Sine.easeOut" });
    }

    const baseLine =
      this.resets > 0
        ? `the dark caught you ${this.resets} time${this.resets === 1 ? "" : "s"} on the way here`
        : "not once did the dark catch you";
    const resetsLine = this.add
      .text(VIEW_WIDTH / 2, VIEW_HEIGHT * 0.885, this.isNewBest ? `${baseLine} - fewest yet` : baseLine, {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "14px",
        color: "#cfc0a0",
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(20);
    this.tweens.add({ targets: resetsLine, alpha: 0.6, duration: 1400, delay: 2800, ease: "Sine.easeOut" });

    const prompt = this.add
      .text(VIEW_WIDTH / 2, VIEW_HEIGHT * 0.94, "press to begin again", {
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: "13px",
        color: "#a9987a",
      })
      .setOrigin(0.5)
      .setAlpha(0)
      .setDepth(20);
    this.tweens.add({
      targets: prompt,
      alpha: { from: 0.25, to: 0.55 },
      duration: 1600,
      delay: 3600,
      yoyo: true,
      repeat: -1,
      ease: "Sine.easeInOut",
    });

    // A target-less tween is this codebase's "wait N ms" - see LevelScene's
    // `after()` for why this.time.delayedCall is avoided here.
    this.tweens.add({
      targets: {},
      duration: 3600,
      onComplete: () => {
        this.input.once(Phaser.Input.Events.POINTER_DOWN, () => this.restart());
        this.input.keyboard!.once("keydown", () => this.restart());
      },
    });

    this.events.once(Phaser.Scenes.Events.POST_UPDATE, () => {
      document.body.dataset.gameReady = "true";
      this.reportState(light);
    });
  }

  private restart(): void {
    this.cameras.main.fadeOut(360, 5, 6, 12);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => {
      this.scene.start("menu");
    });
  }

  private reportState(light: Phaser.GameObjects.Light): void {
    window.__glow = {
      ready: true,
      scene: "ending",
      collected: 0,
      remaining: 0,
      glowRadius: light.radius,
      lightsActive: this.lights.active,
      level: 0,
      resets: this.resets,
      required: 0,
      beaconOpen: false,
      flawless: this.flawless,
      wispX: 0,
      wispY: 0,
      motes: [],
      hazards: [],
    };
  }
}
